import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminExpenses.css';
import { formatCurrency } from '../utils/formatters';

const API = '/api/admin-expenses';
const MASTER_DATA_API = '/api/master-data';
const UPLOAD_URL = '/api/uploads/';

/* ── Custom SVGs ── */
const Icon = ({ name, size = 16, strokeWidth = 1.75 }) => {
    const paths = {
        plus: ['M12 5v14', 'M5 12h14'],
        search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
        edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
        trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
        eye: ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
        cog: ['M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.1a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z', 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
        excel: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M8 13h8', 'M8 17h8'],
        pdf: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8'],
        refresh: ['M21.5 2v6h-6', 'M21.34 15.57a10 10 0 1 1-.57-8.38'],
        wallet: ['M20 12V8H6a2 2 0 0 1 2-2h12V5a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2h-3', 'M20 12h-4a1 1 0 0 0 0 2h4v-2z'],
        grid: ['M3 3h7v7H3z', 'M14 3h7v7h-7z', 'M14 14h7v7h-7z', 'M3 14h7v7H3z'],
        arrowDown: ['M12 5v14', 'M19 12l-7 7-7-7'],
        back: ['M19 12H5', 'M12 19l-7-7 7-7'],
        percent: ['M19 5L5 19', 'M6.5 9.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z', 'M17.5 19.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z']
    };

    const d = paths[name] || [];
    return (
        <svg width={size} height={size} viewBox="0 0 24 24"
            fill="none" stroke="currentColor"
            strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
            {d.map((p, i) => <path key={i} d={p} />)}
        </svg>
    );
};

export default function AdminExpenses() {
    const [expenses, setExpenses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('All');

    const [loading, setLoading] = useState(true);
    const [createPageOpen, setCreatePageOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const country = 'United Arab Emirates'; // Default or based on system setting

    // Form state
    const blankForm = {
        date: new Date().toISOString().split('T')[0],
        amount: '',
        category: '',
        paymentMethod: '',
        payee: '',
        referenceNo: '',
        notes: '',
        attachment: null
    };
    const [formData, setFormData] = useState(blankForm);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchExpenses();
        fetchMasterData();
    }, []);

    const fetchExpenses = async () => {
        setLoading(true);
        try {
            const res = await axios.get(API);
            setExpenses(res.data.expenses);
        } catch (err) {
            console.error('Fetch expenses error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchMasterData = async () => {
        try {
            const res = await axios.get(MASTER_DATA_API);
            const grouped = res.data.reduce((acc, item) => {
                const cat = item.category;
                if (!acc[cat]) acc[cat] = [];
                acc[cat].push(item);
                return acc;
            }, {});
            setCategories(grouped['expense-categories'] || []);
            setPaymentMethods(grouped['payment-methods'] || []);
        } catch (err) {
            console.error('Fetch master data error:', err);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this admin expense?')) return;
        try {
            await axios.delete(`${API}/${id}`);
            fetchExpenses();
        } catch (err) {
            console.error('Delete expense error:', err);
        }
    };

    const openCreate = (item = null) => {
        setEditItem(item);
        if (item) {
            setFormData({
                date: new Date(item.date).toISOString().split('T')[0],
                amount: item.amount,
                category: item.category,
                paymentMethod: item.paymentMethod,
                payee: item.payee || '',
                referenceNo: item.referenceNo || '',
                notes: item.notes || '',
                attachment: null
            });
        } else {
            setFormData(blankForm);
        }
        setCreatePageOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.amount || !formData.category || !formData.paymentMethod) return alert("Amount, Category, and Payment Method are required");
        
        setSaving(true);
        try {
            const data = new FormData();
            ['date', 'amount', 'category', 'paymentMethod', 'payee', 'referenceNo', 'notes'].forEach(k => {
                data.append(k, formData[k]);
            });
            if (formData.attachment) {
                data.append('attachment', formData.attachment);
            }

            if (editItem) {
                await axios.put(`${API}/${editItem._id}`, data);
            } else {
                await axios.post(API, data);
            }
            
            setCreatePageOpen(false);
            fetchExpenses();
        } catch (err) {
            console.error('Error saving admin expense', err);
            alert('Failed to save expense');
        } finally {
            setSaving(false);
        }
    };

    const handleManageCategories = () => {
        window.location.hash = 'expense-categories';
    };

    const handleResetFilters = () => {
        setSearchTerm('');
        setSelectedCategory('');
        setSelectedMonth('All');
        setCurrentPage(1);
    };

    const handleExportExcel = () => {
        const headers = ["Date", "Category", "Payment Method", "Amount", "VAT", "Total", "Payee", "Reference", "Notes"];
        const rows = filtered.map(exp => {
            const dateStr = new Date(exp.date).toLocaleDateString('en-GB');
            const netAmt = exp.amount * 0.95;
            const vatAmt = exp.amount * 0.05;
            return [
                dateStr,
                exp.category,
                exp.paymentMethod || '—',
                netAmt.toFixed(2),
                vatAmt.toFixed(2),
                exp.amount.toFixed(2),
                exp.payee || '—',
                exp.referenceNo || '—',
                exp.notes || '—'
            ];
        });

        let csvContent = "data:text/csv;charset=utf-8,"
            + [headers.join(","), ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))].join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `admin_expenses_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handlePrintPDF = () => {
        window.print();
    };

    const getLast12Months = () => {
        const months = [];
        const date = new Date();
        for (let i = 0; i < 12; i++) {
            const m = date.getMonth();
            const y = date.getFullYear();
            months.push({
                label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
                value: `${y}-${String(m + 1).padStart(2, '0')}`
            });
            date.setMonth(date.getMonth() - 1);
        }
        return months;
    };

    if (createPageOpen) {
        return (
            <div className="admin-expenses-page" style={{ maxWidth: '800px', margin: '0 auto', background: '#fff', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', borderBottom: '1px solid #e2e8f0', paddingBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button onClick={() => setCreatePageOpen(false)} style={{ background: 'none', border: '1px solid #cbd5e1', padding: '8px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                            <Icon name="back" size={16} />
                        </button>
                        <h2 style={{ margin: 0, fontSize: '20px', color: '#0f172a' }}>{editItem ? 'Edit Admin Expense' : 'Record Admin Expense'}</h2>
                    </div>
                </div>

                <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Date *</label>
                            <input type="date" style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }} value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} required />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Amount *</label>
                            <input type="number" step="0.01" style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }} value={formData.amount} onChange={e => setFormData({ ...formData, amount: e.target.value })} required />
                        </div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Category *</label>
                            <select style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }} value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })} required>
                                <option value="">Select Category</option>
                                {categories.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Payment Method *</label>
                            <select style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }} value={formData.paymentMethod} onChange={e => setFormData({ ...formData, paymentMethod: e.target.value })} required>
                                <option value="">Select Method</option>
                                {paymentMethods.map(m => <option key={m._id} value={m.name}>{m.name}</option>)}
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Payee (Optional)</label>
                            <input type="text" placeholder="Who did you pay?" style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }} value={formData.payee} onChange={e => setFormData({ ...formData, payee: e.target.value })} />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Reference / Invoice No (Optional)</label>
                            <input type="text" placeholder="Invoice #, Txn ID, etc." style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }} value={formData.referenceNo} onChange={e => setFormData({ ...formData, referenceNo: e.target.value })} />
                        </div>
                    </div>

                    <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Notes</label>
                        <textarea rows="3" style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box', fontFamily: 'inherit' }} value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })}></textarea>
                    </div>

                    <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Attachment (Optional)</label>
                        <input type="file" style={{ width: '100%', padding: '12px', border: '1px dashed #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }} onChange={e => setFormData({ ...formData, attachment: e.target.files[0] })} />
                        {editItem && editItem.attachment && !formData.attachment && (
                            <div style={{ marginTop: '8px', fontSize: '13px', color: '#10b981' }}>Current attachment saved.</div>
                        )}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
                        <button type="button" onClick={() => setCreatePageOpen(false)} style={{ padding: '12px 24px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                        <button type="submit" disabled={saving} style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
                            {saving ? 'Saving...' : 'Save Expense'}
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    /* ── Filter logic ── */
    const filtered = expenses.filter(exp => {
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            const categoryMatch = exp.category?.toLowerCase().includes(term);
            const notesMatch = exp.notes?.toLowerCase().includes(term);
            const payeeMatch = exp.payee?.toLowerCase().includes(term);
            const refMatch = exp.referenceNo?.toLowerCase().includes(term);
            if (!categoryMatch && !notesMatch && !payeeMatch && !refMatch) return false;
        }
        if (selectedCategory && selectedCategory !== 'All') {
            if (exp.category !== selectedCategory) return false;
        }
        if (selectedMonth && selectedMonth !== 'All') {
            const [y, m] = selectedMonth.split('-');
            const expDate = new Date(exp.date);
            if (expDate.getFullYear() !== parseInt(y) || (expDate.getMonth() + 1) !== parseInt(m)) {
                return false;
            }
        }
        return true;
    });

    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    /* ── Calculations ── */
    const totalSum = filtered.reduce((s, e) => s + (e.amount || 0), 0);
    const totalVat = totalSum * 0.05; // Assuming 5% generic VAT for admin, or logic can be customized
    const totalNet = totalSum - totalVat;

    return (
        <div className="admin-expenses-page">
            <div className="admin-header">
                <div>
                    <h1 className="admin-title">Admin Expenses</h1>
                    <p className="admin-subtitle">Superadmin-level operational cost tracking</p>
                </div>
                <div className="admin-actions">
                    <button className="btn-admin btn-admin-glass" onClick={handleManageCategories}>
                        <Icon name="cog" size={15} /> Categories
                    </button>
                    <button className="btn-admin btn-admin-primary" onClick={() => openCreate()}>
                        <Icon name="plus" size={15} /> Record Expense
                    </button>
                </div>
            </div>

            <div className="admin-kpi-grid">
                <div className="admin-kpi-card kpi-total">
                    <div className="admin-kpi-header">
                        <span>Total Outflow</span>
                        <div className="admin-kpi-icon"><Icon name="arrowDown" size={14} /></div>
                    </div>
                    <div className="admin-kpi-value">{formatCurrency(totalSum, country)}</div>
                    <div className="admin-kpi-subtext">{filtered.length} total records</div>
                </div>
                <div className="admin-kpi-card kpi-vat">
                    <div className="admin-kpi-header">
                        <span>Total VAT</span>
                        <div className="admin-kpi-icon"><Icon name="percent" size={14} /></div>
                    </div>
                    <div className="admin-kpi-value">{formatCurrency(totalVat, country)}</div>
                    <div className="admin-kpi-subtext">Calculated standard VAT</div>
                </div>
                <div className="admin-kpi-card kpi-net">
                    <div className="admin-kpi-header">
                        <span>Net Spend</span>
                        <div className="admin-kpi-icon"><Icon name="wallet" size={14} /></div>
                    </div>
                    <div className="admin-kpi-value">{formatCurrency(totalNet, country)}</div>
                    <div className="admin-kpi-subtext">Excluding taxes</div>
                </div>
                <div className="admin-kpi-card kpi-categories">
                    <div className="admin-kpi-header">
                        <span>Categories</span>
                        <div className="admin-kpi-icon"><Icon name="grid" size={14} /></div>
                    </div>
                    <div className="admin-kpi-value">{categories.length}</div>
                    <div className="admin-kpi-subtext">Active tracking categories</div>
                </div>
            </div>

            <div className="admin-filters">
                <div className="admin-filter-group">
                    <label>Search</label>
                    <input type="text" className="admin-input" placeholder="Payee, notes, ref..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
                </div>
                <div className="admin-filter-group">
                    <label>Category</label>
                    <select className="admin-input" value={selectedCategory} onChange={e => { setSelectedCategory(e.target.value); setCurrentPage(1); }}>
                        <option value="">All</option>
                        {categories.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                    </select>
                </div>
                <div className="admin-filter-group">
                    <label>Month</label>
                    <select className="admin-input" value={selectedMonth} onChange={e => { setSelectedMonth(e.target.value); setCurrentPage(1); }}>
                        <option value="All">All Time</option>
                        {getLast12Months().map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                </div>
                <div className="admin-filter-group" style={{ flexDirection: 'row', alignItems: 'flex-end', gap: '8px' }}>
                    <button className="btn-admin btn-admin-glass" style={{ flex: 1, padding: '10px' }} onClick={handleResetFilters} title="Reset">
                        <Icon name="refresh" size={15} />
                    </button>
                    <button className="btn-admin btn-admin-glass" style={{ flex: 1, padding: '10px', color: '#10b981' }} onClick={handleExportExcel} title="Export Excel">
                        <Icon name="excel" size={15} />
                    </button>
                    <button className="btn-admin btn-admin-glass" style={{ flex: 1, padding: '10px', color: '#ef4444' }} onClick={handlePrintPDF} title="Print PDF">
                        <Icon name="pdf" size={15} />
                    </button>
                </div>
            </div>

            <div className="admin-table-container">
                <div className="admin-table-header">
                    <h2 className="admin-table-title">System Expense Ledger</h2>
                </div>
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Category</th>
                            <th>Payee</th>
                            <th>Reference</th>
                            <th>Amount</th>
                            <th>Method</th>
                            <th>Receipt</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>Loading admin expenses...</td></tr>
                        ) : paginated.length === 0 ? (
                            <tr><td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>No records found.</td></tr>
                        ) : paginated.map(exp => {
                            return (
                                <tr key={exp._id}>
                                    <td>{new Date(exp.date).toLocaleDateString('en-GB')}</td>
                                    <td><span className="admin-badge admin-badge-category">{exp.category}</span></td>
                                    <td style={{ fontWeight: 600 }}>{exp.payee || '—'}</td>
                                    <td style={{ color: '#64748b' }}>{exp.referenceNo || '—'}</td>
                                    <td style={{ fontWeight: 700, color: '#f8fafc' }}>{formatCurrency(exp.amount, country)}</td>
                                    <td>{exp.paymentMethod}</td>
                                    <td>
                                        {exp.attachment ? (
                                            <a href={`${UPLOAD_URL}${exp.attachment}`} target="_blank" rel="noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>
                                                <Icon name="eye" size={14} />
                                            </a>
                                        ) : '—'}
                                    </td>
                                    <td>
                                        <button className="admin-action-btn" onClick={() => openCreate(exp)}><Icon name="edit" size={14} /></button>
                                        <button className="admin-action-btn delete" onClick={() => handleDelete(exp._id)}><Icon name="trash" size={14} /></button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {totalPages > 1 && (
                    <div className="admin-pagination">
                        <div className="admin-page-info">
                            Showing {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length} records
                        </div>
                        <div className="admin-page-controls">
                            <button className="admin-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
                            {[...Array(totalPages)].map((_, i) => (
                                <button key={i} className={`admin-page-btn ${currentPage === i + 1 ? 'active' : ''}`} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                            ))}
                            <button className="admin-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
